<template>
  <v-container>
    <v-card
      class="elevation-1 mt-2"
      :loading="loading"
      :disabled="cardDisabled"
    >
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-col cols="12">
              <p class="display-1 text--primary">{{ $t("Account") }}</p>
              <div class="headline">{{ username }}</div>
            </v-col>
          </v-col>
        </v-row>
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col xs="12" sm="12" md="6">
              <v-col cols="12">
                <v-text-field
                  :label="$t('Name')"
                  :rules="nameRules"
                  v-model="name"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  :label="$t('Surname')"
                  :rules="surnameRules"
                  v-model="surname"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  :label="$t('Email')"
                  :rules="emailRules"
                  v-model="email"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="tzSelect"
                  :items="tzList"
                  :search-input.sync="timezoneSearch"
                  cache-items
                  flat
                  hide-no-data
                  hide-details
                  :label="$t('Timezone')"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12">
                <v-row>
                  <v-col>
                    <v-select
                      v-model="localeSelect"
                      :items="localeList"
                      :label="$t('Language')"
                    ></v-select>
                  </v-col>
                  <v-col>
                    <v-select
                      v-model="darkMode"
                      :items="darkModeList"
                      :label="$t('Dark Mode')"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  name="input-7-1"
                  :label="$t('Description')"
                  :rules="descriptionRules"
                  v-model="description"
                  rows="1"
                  auto-grow
                  dense
                ></v-textarea>
              </v-col>

              <v-col cols="12">
                <v-row>
                  <v-col>
                    <v-btn
                      color="primary"
                      @click="updatePassword"
                      outlined
                      tile
                    >
                      <v-icon left>mdi-sync</v-icon>
                      {{ $t("change password") }}
                    </v-btn>
                  </v-col>
                  <v-col>
                    <v-btn
                      color="primary"
                      :disabled="!valid"
                      :loading="saveLoading"
                      @click="updateUser"
                      outlined
                      tile
                    >
                      <v-icon left>mdi-content-save-outline</v-icon>
                      {{ $t("save") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-col>

            <v-col cols="6" class="d-none d-sm-block">
              <v-card class="d-inline-block mx-auto" :flat="cardFlat">
                <v-container>
                  <v-col>
                    <v-row justify="center">
                      <v-img
                        v-if="avatar"
                        height="200"
                        width="200"
                        class="img-circle"
                        :src="avatar"
                      ></v-img>
                      <avatar
                        v-else
                        :username="username"
                        :size="200"
                        :rounded="true"
                        class="default-avatar"
                        :lighten="90"
                        color="#000"
                        background-color="#EEF0EB"
                      ></avatar>
                    </v-row>
                    <v-row justify="center">
                      <v-col cols="auto">
                        <v-btn
                          class="ma-2"
                          @click="updateImage"
                          tile
                          outlined
                          color="primary"
                        >
                          <v-icon left>mdi-pencil</v-icon>
                          {{ $t("update") }}
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-container>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions></v-card-actions>
    </v-card>

    <UpdatePasswordModal ref="updatePasswordModal"></UpdatePasswordModal>
    <CropImageModal ref="cropImageModal"></CropImageModal>
  </v-container>
</template>

<script>
import axios from "axios";
import i18n from "../i18n";
import Avatar from "vue-avatar";
import EventBus from "../event-bus";
import UpdatePasswordModal from "./Account/UpdatePasswordModal";
import CropImageModal from "./Account/CropImageModal";

export default {
  name: "Account",
  components: {
    Avatar,
    UpdatePasswordModal,
    CropImageModal,
  },
  data: () => ({
    loading: true,
    cardDisabled: true,
    cardFlat: true,
    showPassword: false,
    valid: true,
    saveLoading: false,
    avatar: null,
    username: "",
    password: "",
    passwordRules: [],
    name: "",
    nameRules: [],
    surname: "",
    surnameRules: [],
    email: "",
    emailRules: [],
    description: "",
    descriptionRules: [],
    tzList: [],
    tzSelect: null,
    localeList: [],
    localeSelect: null,
    timezoneSearch: null,
    darkModeList: [],
    darkMode: null,
  }),
  created() {
    this.passwordRules = [
      (v) => !!v || i18n.t("Password is required"),
      (v) => v.length <= 50 || i18n.t("Max 50 characters"),
      (v) => v.length >= 5 || i18n.t("Min 5 characters"),
      (v) => (v || "").indexOf(" ") < 0 || i18n.t("No spaces are allowed"),
    ];
    this.nameRules = [(v) => v.length <= 50 || i18n.t("Max 50 characters")];
    this.surnameRules = [(v) => v.length <= 50 || i18n.t("Max 50 characters")];
    this.emailRules = [
      (v) => !!v || i18n.t("E-mail is required"),
      (v) => /.+@.+/.test(v) || i18n.t("E-mail must be valid"),
    ];
    this.descriptionRules = [
      (v) =>
        typeof v == "undefined" ||
        v.length <= 100 ||
        i18n.t("Max 100 characters"),
    ];
  },
  mounted() {
    this.init();
  },
  watch: {
    timezoneSearch(val) {
      val && val !== this.select && this.timezonesQuerySelections(val);
    },
  },
  methods: {
    init() {
      // Add defaults to timezone and locale.
      this.tzList = this.$tzList.slice();
      this.tzList.unshift(i18n.t("Default"));

      this.localeList = [];

      for (let i = 0; i < this.$localeList.length; i++) {
        this.localeList.push({
          text: i18n.t(this.$localeList[i].text),
          value: this.$localeList[i].value,
        });
      }

      this.localeList.unshift({ text: i18n.t("Default"), value: "Default" });

      this.darkModeList = [
        {
          text: i18n.t("Default"),
          value: null,
        },
        {
          text: i18n.t("Enabled"),
          value: true,
        },
        {
          text: i18n.t("Disabled"),
          value: false,
        },
      ];

      this.updateAccountInfo();
    },
    sendError(error) {
      this.loading = false;

      if (error.response) {
        if (
          error.response.status == 400 &&
          error.response.data.status == "error"
        ) {
          EventBus.$emit("SHOW_ERROR", error.response.data.error);
        } else {
          EventBus.$emit("SHOW_ERROR", error.response.data);
        }
      } else {
        EventBus.$emit("SHOW_ERROR", error);
      }
    },
    updateAccountInfo() {
      axios({ url: "/api/account/user", method: "GET" })
        .then((response) => {
          this.loading = false;
          this.cardDisabled = false;

          if (response.data.status && response.data.status == "success") {
            let account = response.data.account;

            this.username = account.username;

            if ("name" in account) this.name = account.name;

            if ("surname" in account) this.surname = account.surname;

            if ("email" in account) this.email = account.email;

            if ("description" in account)
              this.description = account.description;

            if (account.timezone != "Default") {
              let tz = account.timezone.replace("_", " ");
              this.tzSelect = tz;
            } else {
              this.tzSelect = i18n.t("Default");
            }

            if ("avatar" in account) {
              this.avatar = account.avatar.big;
              this.$store.dispatch("setAvatar", {
                avatar: account.avatar.small,
              });
            } else {
              this.avatar = null;
              this.$store.dispatch("setAvatar", { avatar: null });
            }

            if ("darkMode" in account) {
              this.darkMode = account.darkMode;
            }

            this.localeSelect = account.locale;
          } else {
            EventBus.$emit("SHOW_ERROR", response.data.error);
          }
        })
        .catch((error) => {
          this.sendError(error);
        });
    },
    updateUser() {
      this.saveLoading = true;

      let data = {};

      data.email = this.email;
      data.name = this.name;
      data.surname = this.surname;
      data.description = this.description;

      if (this.tzSelect != i18n.t("Default")) {
        data.timezone = this.tzSelect.replace(" ", "_");
      }

      if (this.localeSelect != "Default") {
        data.locale = this.localeSelect;
      }

      if (this.darkMode != null) {
        data.darkMode = this.darkMode;
      }

      axios({
        url: "/api/account/user",
        headers: { "Content-Type": "application/json" },
        data: data,
        method: "PUT",
      })
        .then((response) => {
          this.saveLoading = false;

          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "success")
          ) {
            EventBus.$emit("SHOW_ERROR", response);
          } else {
            this.updateAccountInfo();
          }
        })
        .catch((error) => {
          this.saveLoading = false;
          this.sendError(error);
        });
    },
    timezonesQuerySelections(v) {
      this.tzlist = this.$tzList.filter((e) => {
        return (e || "").toLowerCase().indexOf((v || "").toLowerCase()) > -1;
      });
    },
    updatePassword() {
      this.$refs.updatePasswordModal.show();
    },
    updateImage() {
      this.$refs.cropImageModal.show();
    },
  },
};
</script>

<style scoped>
.img-circle {
  border-radius: 50%;
}
</style>